import { Component, EventEmitter, forwardRef, HostBinding, inject, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Delta } from 'quill/core';
import { SvcQuillTextEditorComponent } from '@solvace/component/svc-controls/svc-quill-text-editor';
import { Mention, SvcCommentMentionType } from '../../../svc-quill-editor-mention';
import { SvcCommentsService } from '../../services/svc-comments.service';
import { SvcCommentsChatComponent } from '../svc-comments-chat/svc-comments-chat.component';

@Component({
  selector: 'svc-comments-input',
  templateUrl: './svc-comments-input.component.html',
  styleUrls: ['./svc-comments-input.component.scss'],
  providers: [SvcCommentsService],
})
export class SvcCommentsInputComponent {
  @Input() control: AbstractControl;
  @Input() readOnly: boolean;
  @Input() placeholder: string;
  @Input() cursorForce: boolean;
  @Input() loading: boolean;
  @Input() buttonDisabled: boolean;
  /**
   * AB#60317 — hides the send (paper-plane) button and its Ctrl+Enter shortcut, for hosts that drive
   * the send themselves (CIL execution answer: Save posts the mandatory justification).
   */
  @HostBinding('class.hide-send-button') @Input() hideSendButton = false;

  @Output() onButtonClicked = new EventEmitter<void>();

  @ViewChild(SvcQuillTextEditorComponent) editor: SvcQuillTextEditorComponent;

  public parent: SvcCommentsChatComponent = inject(forwardRef(() => SvcCommentsChatComponent));

  public get allMentions(): Mention[] {
    return this.editor?.extractMentions();
  }

  get text(): string {
    return this.editor.text;
  }

  get html(): string {
    return this.editor.html;
  }

  get deltas(): Delta {
    return this.editor.deltas;
  }
  get mentions(): Mention[] {
    return this.allMentions
      .filter((x) => x.id != null)
      .map((x) => ({
        ...x,
        type: typeof x.type === 'string' ? parseInt(x.type) : x.type,
      }));
  }

  protected onEditorRender(): void {
    this.editor?.editorInstance?.root.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        if (!this.loading && !this.buttonDisabled && !this.hideSendButton) {
          event.preventDefault();
          this.onButtonClicked.emit();
        }
      }
    });
  }

  public focus(): void {
    setTimeout(() => this.editor?.focus());
  }

  public setDisabled(isDisabled: boolean): void {
    isDisabled ? this.editor?.disable() : this.editor?.enable();
  }

  public addMention(type: SvcCommentMentionType, id: number | string, name: string): void {
    this.addMentions([
      {
        id,
        name,
        type,
      },
    ]);
  }

  public addMentions(mentions: { type: SvcCommentMentionType; id: number | string; name: string }[]): void {
    this.editor?.insertMentions<Mention>(
      mentions.map((m) => ({
        denotationChar: '@',
        id: m.id,
        value: m.name,
        type: m.type,
      }))
    );
  }
}